import React from 'react';
import BlueCallToAction from '../components/common/BlueCallToAction';

/**
 * WebinarsPage component
 * Showcases upcoming and past webinars, training sessions
 */
const WebinarsPage = () => {
  const upcomingWebinars = [
    {
      title: "Healthcare AI Implementation Best Practices",
      date: "April 10, 2026",
      time: "2:00 PM - 3:00 PM EST",
      presenter: "Dr. Sarah Chen, Chief Medical Officer",
      description: "Learn proven strategies for successfully implementing AI solutions in healthcare settings.",
      registrationOpen: true,
      attendees: 142
    },
    {
      title: "Revenue Cycle Optimization with AI",
      date: "April 17, 2026",
      time: "1:00 PM - 2:30 PM EST",
      presenter: "Michael Rodriguez, Revenue Cycle Director",
      description: "Discover how AI can streamline your revenue cycle and reduce claim denials.",
      registrationOpen: true,
      attendees: 98
    },
    {
      title: "Advanced Claims Processing Automation",
      date: "April 24, 2026",
      time: "3:00 PM - 4:00 PM EST",
      presenter: "Jennifer Walsh, Solutions Architect",
      description: "Deep dive into automated claims processing workflows and integration strategies.",
      registrationOpen: true,
      attendees: 67
    }
  ];

  const pastWebinars = [
    {
      title: "Introduction to Healthcare AI Platform",
      date: "March 15, 2026",
      presenter: "David Kim, Product Manager",
      description: "Overview of platform capabilities and getting started guide.",
      views: 324,
      rating: 4.8
    },
    {
      title: "HIPAA Compliance in AI Healthcare Solutions",
      date: "February 28, 2026",
      presenter: "Lisa Thompson, Compliance Officer",
      description: "Ensuring patient data protection and regulatory compliance.",
      views: 256,
      rating: 4.9
    },
    {
      title: "Integration Strategies for EHR Systems",
      date: "February 14, 2026",
      presenter: "Alex Martinez, Technical Lead",
      description: "Best practices for integrating with Epic, Cerner, and other EHR systems.",
      views: 198,
      rating: 4.7
    },
    {
      title: "ROI Measurement in Healthcare AI",
      date: "January 30, 2026",
      presenter: "Rachel Green, Business Analyst",
      description: "Methods for measuring and demonstrating return on investment.",
      views: 289,
      rating: 4.8
    }
  ];

  const webinarSeries = [
    {
      title: "AI Fundamentals for Healthcare",
      description: "4-part series covering the basics of AI implementation in healthcare",
      sessions: 4,
      duration: "1 hour each",
      level: "Beginner",
      nextSession: "May 6, 2026"
    },
    {
      title: "Advanced Revenue Cycle Management",
      description: "Deep dive into optimizing revenue cycles with AI and automation",
      sessions: 6,
      duration: "90 minutes each",
      level: "Advanced",
      nextSession: "May 13, 2026"
    },
    {
      title: "Technical Implementation Workshop",
      description: "Hands-on technical training for IT professionals and developers",
      sessions: 8,
      duration: "2 hours each",
      level: "Expert",
      nextSession: "May 20, 2026"
    }
  ];

  return (
    <div className="webinars-page">
      {/* Hero Section */}
      <section 
        className="py-20"
        style={{
          background: `linear-gradient(to top, #FFC000 0%, #FFFFFF 3%, #FFFFFF 97%, #FFC000 100%)`
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 
              className="text-5xl md:text-6xl font-bold mb-6" 
              style={{ color: '#111827' }}
            >
              Webinars & <span style={{ color: '#FFC000' }}>Training</span>
            </h1>
            <p 
              className="text-xl md:text-2xl mb-8" 
              style={{ color: '#374151' }}
            >
              Join our expert-led webinars and training sessions to maximize
              your platform knowledge and implementation success.
            </p>
          </div>
        </div>
      </section>

      {/* Upcoming Webinars Section */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            Upcoming Webinars
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {upcomingWebinars.map((webinar, index) => (
              <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 shadow-sm">
                <div className="mb-4">
                  <div className="text-sm text-primary-600 dark:text-primary-400 font-medium mb-2">
                    {webinar.date} • {webinar.time}
                  </div>
                  <h3 className="text-xl font-semibold mb-2 text-gray-900 dark:text-white">
                    {webinar.title}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-300 mb-4">
                    {webinar.description}
                  </p>
                </div>
                <div className="mb-4">
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    Presenter: <span className="font-medium">{webinar.presenter}</span>
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {webinar.attendees} registered
                  </p>
                </div>
                <button className="w-full bg-primary-600 hover:bg-primary-700 text-white py-2 px-4 rounded-lg transition-colors">
                  Register Now
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Webinar Series Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            Training Series
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {webinarSeries.map((series, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm">
                <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                  {series.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {series.description}
                </p>
                <div className="space-y-2 mb-6">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Sessions:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{series.sessions}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Duration:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{series.duration}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Level:</span>
                    <span className={`px-2 py-1 rounded text-xs ${
                      series.level === 'Beginner' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200' :
                      series.level === 'Advanced' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                      'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                    }`}>
                      {series.level}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Next Session:</span>
                    <span className="font-medium text-gray-900 dark:text-white">{series.nextSession}</span>
                  </div>
                </div>
                <button className="w-full border-2 border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white py-2 px-4 rounded-lg transition-colors">
                  Learn More
                </button>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Past Webinars Section */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            Past Webinars
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {pastWebinars.map((webinar, index) => (
              <div key={index} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 shadow-sm">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-lg font-semibold mb-2 text-gray-900 dark:text-white">
                      {webinar.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
                      {webinar.date} • {webinar.presenter}
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="flex items-center text-sm text-gray-600 dark:text-gray-400">
                      ⭐ {webinar.rating}
                    </div>
                  </div>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  {webinar.description}
                </p>
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    {webinar.views} views
                  </span>
                  <button className="text-primary-600 dark:text-primary-400 hover:underline">
                    Watch Recording →
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call-to-Action Section */}
      <BlueCallToAction 
        heading="Want to host a custom training session?"
        description="We can arrange private training sessions tailored to your organization's specific needs."
        primaryButtonText="Request Custom Training"
        primaryButtonLink="/contact"
        secondaryButtonText="View All Recordings"
        secondaryButtonLink="#"
      />
    </div>
  );
};

export default WebinarsPage;